import { action, computed, observable, runInAction } from 'mobx';
import type { NavigateFunction } from 'react-router';
import { getAppConfig } from '../config/app';
import { myCsUrl } from '../config/mycs';
import { storageService } from '../services/storage.service';
import { initializeUnleashClient } from '../services/unleash-feature-flags.service';
import { AppConfig } from '../types/app-config';
import { PracticeOffersResponse, PracticeResponse } from '../types/practices-types';
import {
  DropDownOption,
  PracticeSpeciesBreeds,
  VendorSpeciesBreeds,
} from '../types/species-breeds-types';
import type { Sap } from '../types/users-types';
import { isDefined, sortByField } from '../utils/object-utils';
import AbstractStore from './AbstractStore';
import RootStore from './RootStore';

export const MODAL_ID = 'global-modal';

const LOGGING_OUT = 'LOGGING_OUT';

interface StepsCompleted {
  practices?: boolean;
  offers?: boolean;
  species?: boolean;
}

export default class GlobalStore extends AbstractStore {
  public constructor(rootStore: RootStore) {
    super(rootStore);
    this._loggingOut = storageService.getItem(LOGGING_OUT, true) || false;
  }

  @observable accessor appConfig: AppConfig | null = null;

  @observable accessor _hasUnsavedChanges = false;

  @observable accessor practiceSelectorDisabled = false;

  @observable accessor isLoggedIn = false;

  @observable accessor isLoading = true;

  @observable accessor practices: PracticeResponse[] = [];

  @observable accessor practiceOffers: PracticeOffersResponse[] = [];

  @observable accessor practiceSpeciesBreeds: PracticeSpeciesBreeds[] = [];

  @observable accessor vendorSpeciesBreeds: Record<string, VendorSpeciesBreeds> = {};

  @observable accessor speciesMappingChanged = false;

  @observable accessor leftNavPanelShown = false;

  @observable accessor stepsCompleted: StepsCompleted = {};

  @observable private accessor _selectedSap: Sap = { id: '', name: '' };

  @observable private accessor _loggingOut = false;

  @computed
  public get loggingOut(): boolean {
    return this._loggingOut;
  }

  @computed
  get selectedSap(): Sap {
    if (!this._selectedSap.id) {
      const sap = storageService.getItem('SAP', false);
      if (sap) this._selectedSap = sap;
    }
    return this._selectedSap;
  }

  @computed
  public get userSaps(): Sap[] {
    return sortByField(this.root.apiStore.user?.saps || [], 'id');
  }

  @computed
  public get practicesRetrieved(): boolean {
    return this.practices.length > 0;
  }

  @computed
  public get practicesConnected(): boolean {
    return this.practices.some(p => isDefined(p.hospitalId));
  }

  @computed
  public get vendorBreedsDropdownValues(): Record<string, DropDownOption[]> {
    const result: Record<string, DropDownOption[]> = {};
    Object.values(this.vendorSpeciesBreeds).forEach(
      s =>
        (result[s.id] = (s?.breeds || []).map(b => ({
          value: b.id,
          label: `${s?.name}, ${b.name}`,
        }))),
    );
    return result;
  }

  set selectedSap(value: Sap | null) {
    if (value) {
      storageService.update('SAP', value, false);
      this._selectedSap = value;
    } else {
      storageService.removeItem('SAP', false);
      this._selectedSap = { id: '', name: '' };
    }
  }

  public set loggingOut(value: boolean) {
    this._loggingOut = value;
    storageService.replace(LOGGING_OUT, value, true);
  }

  @action
  public setLoading(value: boolean): void {
    this.isLoading = value;
  }

  @action
  public setPractices(practices: PracticeResponse[]): void {
    const primary: PracticeResponse[] = [];
    const nonPrimary: PracticeResponse[] = [];
    practices.forEach(p => {
      if (p.isPrimary) primary.push(p);
      else nonPrimary.push(p);
    });
    this.practices = [...sortByField(primary, 'name1'), ...sortByField(nonPrimary, 'name1')];
  }

  public _navigate: NavigateFunction = () => {};

  public async init(navigate: NavigateFunction): Promise<void> {
    runInAction(() => (this._navigate = navigate));
    const appConfig = await getAppConfig();
    if (appConfig) {
      runInAction(() => (this.appConfig = appConfig));
      await initializeUnleashClient({
        appName: this.appConfig!.unleashFeatureFlagsAppName,
        clientKey: this.appConfig!.unleashFeatureFlagsClientKey,
        url: this.appConfig!.unleashFeatureFlagsUrl,
        context: { userId: this.root.apiStore.userEmail, sessionId: this.root.apiStore.userGuid },
        refreshInterval: 60,
      });
    }
  }

  public toMyCsDashboard(): void {
    window.location.href = myCsUrl();
  }

  public async signOut(): Promise<void> {
    this.selectedSap = null;
    for (const storeName of Object.keys(this.root)) {
      const store = this.root[storeName] as AbstractStore;
      if (!(store instanceof GlobalStore)) await store.signOut();
    }
  }
}
